import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { ShieldAlert, ScrollText, CheckCircle2 } from 'lucide-react';

const CATEGORIES = [
  { slug: 'licensing', label: 'Licensing' }, { slug: 'careers', label: 'Careers' },
  { slug: 'supervision', label: 'Supervision' }, { slug: 'internships', label: 'Internships' },
  { slug: 'documentation', label: 'Documentation' }, { slug: 'private-practice', label: 'Private Practice' },
  { slug: 'medical-social-work', label: 'Medical Social Work' }, { slug: 'school-social-work', label: 'School Social Work' },
];

const AskQuestion = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('edit');
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [form, setForm] = useState({ title: '', body: '', category_slug: 'licensing', tags: '', state: '', specialty: '', use_nickname: false, rules_confirmed: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { document.title = 'Ask a Question | Ask a Social Worker'; }, []);
  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  const loadForEdit = useCallback(async () => {
    if (!editId) return;
    try {
      const r = await axios.get(`${API_URL}/community/my-questions`);
      const q = (r.data.questions || []).find((x) => x.question_id === editId);
      if (q) setForm({ title: q.title, body: q.body, category_slug: q.category_slug, tags: (q.tags || []).join(', '), state: q.state || '', specialty: q.specialty || '', use_nickname: q.use_nickname || false, rules_confirmed: false });
    } catch (e) { /* noop */ }
  }, [editId]);
  useEffect(() => { loadForEdit(); }, [loadForEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (form.title.trim().length < 8) { toast.error('Please write a clearer, longer title.'); return; }
    if (form.body.trim().length < 20) { toast.error('Please add more detail to your question.'); return; }
    if (!form.rules_confirmed) { toast.error('Please confirm the community rules before submitting.'); return; }
    setSubmitting(true);
    const payload = { ...form, tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean) };
    try {
      let res;
      if (editId) res = await axios.put(`${API_URL}/community/questions/${editId}`, payload);
      else res = await axios.post(`${API_URL}/community/questions`, payload);
      if (res.data.emergency) toast.warning('If you or someone is in crisis, call or text 988 now.');
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit your question.');
    } finally { setSubmitting(false); }
  };

  if (submitted) return (
    <div className="min-h-screen bg-[#F7F5EF] pt-28 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-2xl border p-8 text-center" data-testid="ask-success">
        <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-3" />
        <h1 className="font-serif text-2xl font-bold text-primary">Submitted for review</h1>
        <p className="text-gray-600 mt-2">Your question was sent to our moderators. To protect the community, every question is reviewed before it's published. You'll be notified once it's approved.</p>
        <div className="flex justify-center gap-3 mt-5">
          <Button onClick={() => navigate('/community/my-activity')} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="go-my-activity">My Community Activity</Button>
          <Button onClick={() => navigate('/community/questions')} variant="outline">Browse Questions</Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <section className="bg-primary pt-24 pb-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <h1 className="font-serif text-3xl font-bold">{editId ? 'Edit Your Question' : 'Ask the Community'}</h1>
          <p className="text-white/80 mt-2">Post a question for fellow social workers. Every question is reviewed by a moderator before publishing.</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
        {/* Disclaimers */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-800 mb-5">
          <ShieldAlert className="w-5 h-5 flex-shrink-0 text-amber-600" />
          <div>
            <p className="font-semibold">Before you post</p>
            <ul className="list-disc ml-4 mt-1 space-y-0.5">
              <li>Never include client-identifying information — change any identifying details.</li>
              <li>This community does not provide legal or emergency clinical advice.</li>
              <li>In a crisis, call or text <strong>988</strong> or your local emergency services immediately.</li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-2xl border p-6 space-y-5">
          <div>
            <Label>Question title</Label>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Summarize your question in one line" data-testid="ask-title-input" />
          </div>
          <div>
            <Label>Category</Label>
            <select className="w-full border rounded-md h-10 px-3 text-sm bg-white" value={form.category_slug} onChange={(e) => set('category_slug', e.target.value)} data-testid="ask-category-select">
              {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Details</Label>
            <Textarea rows={7} value={form.body} onChange={(e) => set('body', e.target.value)} placeholder="Share the context and what you'd like help with. Keep it general — no client-identifying details." data-testid="ask-body-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>State (optional)</Label>
              <Input value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="e.g. Texas" data-testid="ask-state-input" />
            </div>
            <div>
              <Label>Specialty (optional)</Label>
              <Input value={form.specialty} onChange={(e) => set('specialty', e.target.value)} placeholder="e.g. Hospice" data-testid="ask-specialty-input" />
            </div>
          </div>
          <div>
            <Label>Tags (optional, comma-separated)</Label>
            <Input value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder="e.g. LCSW, supervision hours" data-testid="ask-tags-input" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="use_nickname" checked={form.use_nickname} onCheckedChange={(v) => set('use_nickname', !!v)} data-testid="ask-nickname-checkbox" />
            <Label htmlFor="use_nickname" className="font-normal cursor-pointer">Post as "Community Member" instead of my name</Label>
          </div>
          <div className="flex items-start gap-2 bg-primary/5 rounded-lg p-3">
            <Checkbox id="rules_confirmed" checked={form.rules_confirmed} onCheckedChange={(v) => set('rules_confirmed', !!v)} data-testid="ask-rules-checkbox" className="mt-0.5" />
            <Label htmlFor="rules_confirmed" className="font-normal cursor-pointer text-sm">
              <ScrollText className="w-4 h-4 inline mr-1 text-primary" />
              I confirm this question contains no client-identifying information and follows the community guidelines. I understand it is not a request for legal or emergency advice.
            </Label>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate('/community/questions')}>Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="ask-submit-btn">{submitting ? 'Submitting…' : 'Submit for Review'}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AskQuestion;
